import { Customer } from "@redotech/redo-customer-common/customer";
import { ReviewCreatedEvent } from "@redotech/redo-customer-event-common/customer-event-definition";
import {
  defineSchema,
  SchemaInstance,
} from "@redotech/redo-flows-core-schema-common/type-system/schema";

export const judgemeReviewSubmittedSchema = defineSchema({
  fields: {
    customerEventId: {
      dataType: "Maybe Text",
      documentation: "The submitted review event identifier.",
      hideFromConditionStep: true,
      hideFromInsertionModal: true,
    },
    redoCustomerId: {
      dataType: "Maybe Text",
      documentation: "Redo's identifier for the reviewer.",
      hideFromConditionStep: true,
      hideFromInsertionModal: true,
    },
    customerEmail: {
      dataType: "Maybe Email",
      documentation: "The email address of the reviewer.",
      hideFromConditionStep: true,
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation: "The phone number of the reviewer.",
      hideFromConditionStep: true,
    },
    customerFirstName: {
      dataType: "Maybe Text",
      documentation: "The first name of the reviewer.",
      hideFromConditionStep: true,
    },
    customerLastName: {
      dataType: "Maybe Text",
      documentation: "The last name of the reviewer.",
      hideFromConditionStep: true,
    },
    rating: {
      dataType: "Maybe Number",
      label: "Rating",
      documentation: "The star rating on the Judge.me review.",
    },
    verified: {
      dataType: "Maybe Boolean",
      label: "Verified",
      documentation: "Whether Judge.me marked the reviewer as verified.",
    },
    productId: {
      dataType: "Maybe Text",
      label: "Product ID",
      documentation: "The store product id on the Judge.me review.",
    },
    productTitle: {
      dataType: "Maybe Text",
      label: "Product title",
      documentation: "The product title on the Judge.me review.",
    },
    reviewTitle: {
      dataType: "Maybe Text",
      label: "Review title",
      documentation: "The title the reviewer wrote.",
    },
    reviewContent: {
      dataType: "Maybe Text",
      label: "Review content",
      documentation: "The body of the review.",
    },
    reviewCreatedAt: {
      dataType: "Maybe DateTime",
      label: "Review created at",
      documentation: "When the review was created in Judge.me.",
      hideFromConditionStep: true,
    },
  },
});

export const exampleJudgemeReviewSubmittedSchemaInstance: SchemaInstance<
  typeof judgemeReviewSubmittedSchema
> = {
  customerEventId: "67f8d6d38fd4c4b4d1c19876",
  redoCustomerId: "507f1f77bcf86cd799439011",
  customerEmail: "reviewer@example.com",
  customerPhone: "+15555551234",
  customerFirstName: "Jane",
  customerLastName: "Doe",
  rating: 5,
  verified: true,
  productId: "1234567890",
  productTitle: "Example product",
  reviewTitle: "Great product",
  reviewContent: "I really loved this product.",
  reviewCreatedAt: Temporal.Instant.from("2026-01-15T12:00:00.000Z"),
};

function optionalInstant(value: unknown): Temporal.Instant | undefined {
  if (value instanceof Date) {
    return Temporal.Instant.fromEpochMilliseconds(value.getTime());
  }
  if (typeof value !== "string" || value.length === 0) {
    return undefined;
  }
  try {
    return Temporal.Instant.from(value);
  } catch {
    return undefined;
  }
}

export function judgemeReviewSubmittedToSchemaInstance(
  event: ReviewCreatedEvent,
  customer: Customer | null,
): SchemaInstance<typeof judgemeReviewSubmittedSchema> {
  return {
    customerEventId: event._id.toString(),
    redoCustomerId: customer?._id?.toString() ?? event.customer,
    customerEmail: event.email ?? customer?.emails?.[0]?.email,
    customerPhone:
      customer?.phoneNumbers?.[0]?.phoneNumber ?? event.phoneNumber,
    customerFirstName: customer?.firstName ?? undefined,
    customerLastName: customer?.lastName ?? undefined,
    rating: event.score,
    verified: event.verifiedBuyer,
    productId: event.shopifyProductId,
    productTitle: event.productTitle,
    reviewTitle: event.title,
    reviewContent: event.content,
    reviewCreatedAt: optionalInstant(event.timestamp),
  };
}
